/**
 * BiometricGate — when the user enabled fingerprint unlock (native), this gates
 * the whole app behind a biometric prompt that fires automatically on open.
 * On the web (or when biometric is off) it renders children directly.
 *
 * On mobile, the NATIVE biometric dialog renders on top of this screen — so
 * the wait state stays essentially empty (theme-aware background only, at
 * most a tiny discreet spinner) instead of duplicating UI the OS already
 * shows. A small fallback only appears after a failed/cancelled attempt, so
 * no one gets stuck behind it.
 */
import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { MoonLoader } from './ui/moon-loader';
import { isBiometricEnabled, unlockWithBiometric, disableBiometric } from '../lib/biometric';
import { useAuthStore } from '../stores/auth-store';

function lockActive(): boolean {
  return isBiometricEnabled();
}

export function BiometricGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [locked, setLocked] = useState<boolean>(() => lockActive());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const logout = useAuthStore((s) => s.logout);

  const tryUnlock = useCallback(async () => {
    setBusy(true);
    setFailed(false);
    const token = await unlockWithBiometric();
    setBusy(false);
    if (token) setLocked(false);
    else setFailed(true);
  }, []);

  // Auto-prompt on open — slight delay so the launch animation plays first.
  useEffect(() => {
    if (!locked) return;
    const t = window.setTimeout(tryUnlock, 1600);
    return () => window.clearTimeout(t);
  }, [locked, tryUnlock]);

  const usePassword = useCallback(async () => {
    await disableBiometric();
    await logout();
    setLocked(false);
  }, [logout]);

  if (!locked) return <>{children}</>;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-4 bg-background"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Idle wait: nothing but the theme-aware background — the native prompt
          covers this. A tiny, delayed, low-opacity spinner only surfaces if
          the OS dialog takes a moment to resolve. */}
      {busy && !failed && (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          aria-hidden
        >
          <MoonLoader size={20} />
        </motion.span>
      )}

      <AnimatePresence>
        {failed && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={{ duration: 0.35 }}
            className="flex flex-col items-center gap-3"
          >
            <button
              type="button"
              onClick={tryUnlock}
              className="text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              {t('biometricGate.retry', 'Tentar novamente')}
            </button>
            <button
              type="button"
              onClick={usePassword}
              className="text-xs font-medium text-muted-foreground/70 underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              {t('biometricGate.usePassword', 'Usar senha')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <span role="status" aria-live="polite" className="sr-only">
        {failed
          ? t('biometricGate.failedSr', 'Verificação biométrica falhou.')
          : t('biometricGate.verifyingSr', 'Verificando biometria…')}
      </span>
    </div>
  );
}
